<template>
    <v-container class="d-flex justify-center align-center" style="min-height: 100vh">
        <!-- Loading -->
        <div v-if="loading" class="text-center">
            <v-progress-circular indeterminate size="64"></v-progress-circular>
        </div>

        <!-- Not Found -->
        <v-card v-else-if="notFound" max-width="500" class="text-center pa-6">
            <v-icon size="64" color="error" class="mb-4">mdi-alert-circle-outline</v-icon>
            <v-card-title>Quiz Not Found</v-card-title>
            <v-card-text>This quiz link is invalid or has expired.</v-card-text>
        </v-card>

        <!-- Pending: Welcome Screen -->
        <v-card v-else-if="attempt && attempt.status === 0" max-width="600" class="pa-4">
            <v-card-title class="text-h5 text-center pb-2">{{ attempt.quiz.name }}</v-card-title>
            <v-divider class="mb-4"></v-divider>
            <v-card-text class="text-center">
                <p class="text-h6 mb-4">Welcome, {{ attempt.participantName }}!</p>
                <p v-if="attempt.quiz.welcomeMessage" class="mb-4">{{ attempt.quiz.welcomeMessage }}</p>
                <v-list density="compact" class="mb-4 text-left">
                    <v-list-item prepend-icon="mdi-help-circle-outline">
                        <v-list-item-title>{{ attempt.quiz.questions.length }} questions</v-list-item-title>
                    </v-list-item>
                    <v-list-item prepend-icon="mdi-clock-outline">
                        <v-list-item-title>{{ attempt.quiz.totalTimeMinutes }} minutes to complete</v-list-item-title>
                    </v-list-item>
                    <v-list-item prepend-icon="mdi-information-outline">
                        <v-list-item-title>All questions are multiple choice</v-list-item-title>
                    </v-list-item>
                </v-list>
                <p class="text-caption text-medium-emphasis mb-4">
                    The timer starts when you click Start. Complete all questions before time runs out.
                </p>
            </v-card-text>
            <v-card-actions class="justify-center">
                <v-btn color="primary" size="large" :loading="starting" @click="startQuiz">
                    Start Quiz
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- In Progress: Quiz Questions -->
        <v-card v-else-if="attempt && attempt.status === 1" max-width="700" width="100%">
            <!-- Timer bar -->
            <v-card-text class="pb-0">
                <div class="d-flex align-center justify-space-between mb-2">
                    <span class="text-caption text-medium-emphasis">
                        Question {{ currentIndex + 1 }} of {{ attempt.quiz.questions.length }}
                    </span>
                    <span :class="['font-weight-bold', timerUrgent ? 'text-error' : 'text-primary']">
                        <v-icon size="small" class="mr-1">mdi-clock-outline</v-icon>
                        {{ formattedTimeLeft }}
                    </span>
                </div>
                <v-progress-linear
                    :model-value="timerPercent"
                    :color="timerUrgent ? 'error' : 'primary'"
                    height="4"
                    rounded
                    class="mb-4"
                ></v-progress-linear>

                <!-- Question -->
                <div class="text-h6 mb-4">{{ currentQuestion.name }}</div>

                <!-- Options -->
                <v-radio-group v-model="selectedOptions[currentQuestion.id]" class="mt-0">
                    <v-radio
                        v-for="option in currentQuestion.options"
                        :key="option.id"
                        :label="option.name"
                        :value="option.id"
                        class="mb-2"
                    ></v-radio>
                </v-radio-group>
            </v-card-text>

            <v-card-actions class="px-4 pb-4">
                <v-btn
                    variant="text"
                    :disabled="currentIndex === 0"
                    @click="prevQuestion"
                >
                    Back
                </v-btn>
                <v-spacer></v-spacer>
                <span class="text-caption text-medium-emphasis">
                    {{ answeredCount }} / {{ attempt.quiz.questions.length }} answered
                </span>
                <v-spacer></v-spacer>
                <v-btn
                    v-if="currentIndex < attempt.quiz.questions.length - 1"
                    color="primary"
                    @click="nextQuestion"
                >
                    Next
                </v-btn>
                <v-btn
                    v-else
                    color="success"
                    :loading="completing"
                    @click="confirmComplete"
                >
                    Submit
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Expired -->
        <v-card v-else-if="attempt && attempt.status === 3" max-width="500" class="text-center pa-6">
            <v-icon size="64" color="warning" class="mb-4">mdi-clock-alert-outline</v-icon>
            <v-card-title>Time's Up!</v-card-title>
            <v-card-text>Your time ran out. The quiz has been submitted with your current answers.</v-card-text>
        </v-card>

        <!-- Completed: Results -->
        <v-card v-else-if="attempt && attempt.status === 2" max-width="700" width="100%">
            <v-card-title class="text-h5 text-center pt-6 pb-2">Quiz Complete!</v-card-title>
            <v-card-subtitle class="text-center mb-4">
                <v-chip color="primary" class="mr-2">
                    Score: {{ attempt.correctAnswers }} / {{ attempt.totalAnswered }}
                </v-chip>
                <v-chip color="secondary">
                    Time: {{ attempt.timeTakenSeconds != null ? formatTime(attempt.timeTakenSeconds) : '-' }}
                </v-chip>
            </v-card-subtitle>
            <v-divider></v-divider>
            <v-card-text>
                <div
                    v-for="(answer, i) in attempt.answers"
                    :key="answer.quizQuestionId"
                    class="d-flex align-start mb-2 rounded pa-3"
                    :style="answer.isCorrect ? 'border-left: 4px solid #4caf50; background: rgba(76,175,80,0.05)' : 'border-left: 4px solid #f44336; background: rgba(244,67,54,0.05)'"
                >
                    <v-icon :color="answer.isCorrect ? 'success' : 'error'" class="mr-3 mt-1 flex-shrink-0">
                        {{ answer.isCorrect ? 'mdi-check-circle' : 'mdi-close-circle' }}
                    </v-icon>
                    <div>
                        <div class="text-body-1 font-weight-medium">Q{{ i + 1 }}. {{ answer.questionName }}</div>
                        <div class="text-body-2 text-medium-emphasis mt-1">
                            <span>Your answer: <strong :class="answer.isCorrect ? 'text-success' : 'text-error'">{{ answer.selectedOptionName || 'Not answered' }}</strong></span>
                            <span v-if="!answer.isCorrect" class="ml-3">
                                Correct: <strong class="text-success">{{ answer.correctOptionName }}</strong>
                            </span>
                            <span class="text-medium-emphasis ml-3">{{ answer.timeSpentSeconds }}s</span>
                        </div>
                    </div>
                </div>
            </v-card-text>
        </v-card>

        <!-- Confirm Submit Dialog -->
        <v-dialog v-model="showConfirmComplete" max-width="400">
            <v-card>
                <v-card-title>Submit Quiz?</v-card-title>
                <v-card-text>
                    You have answered {{ answeredCount }} of {{ attempt?.quiz?.questions?.length ?? 0 }} questions.
                    <span v-if="answeredCount < (attempt?.quiz?.questions?.length ?? 0)" class="text-warning">
                        Unanswered questions will be marked incorrect.
                    </span>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn @click="showConfirmComplete = false">Cancel</v-btn>
                    <v-btn color="success" :loading="completing" @click="completeQuiz">Submit</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import axios from 'axios';

export default {
    name: 'QuizTakeView',
    props: { token: String },
    data() {
        return {
            attempt: null,
            loading: false,
            notFound: false,
            starting: false,
            completing: false,
            currentIndex: 0,
            selectedOptions: {},
            questionStartTime: null,
            questionTimeSpent: {},
            timeLeft: 0,
            timerInterval: null,
            totalTime: 0,
            showConfirmComplete: false,
            savingAnswer: false
        };
    },
    mounted() {
        this.loadAttempt();
    },
    beforeUnmount() {
        this.stopTimer();
    },
    computed: {
        currentQuestion() {
            return this.attempt?.quiz?.questions?.[this.currentIndex] ?? null;
        },
        answeredCount() {
            return Object.keys(this.selectedOptions).filter(k => this.selectedOptions[k] != null).length;
        },
        formattedTimeLeft() {
            const m = Math.floor(this.timeLeft / 60);
            const s = this.timeLeft % 60;
            return `${m}:${String(s).padStart(2, '0')}`;
        },
        timerPercent() {
            if (!this.totalTime) return 100;
            return (this.timeLeft / this.totalTime) * 100;
        },
        timerUrgent() {
            return this.timeLeft <= 60;
        }
    },
    methods: {
        loadAttempt() {
            this.loading = true;
            const apiUrl = process.env.VUE_APP_API_URL;
            axios.get(`${apiUrl}quiz/take/${this.token}`)
                .then(res => {
                    this.attempt = res.data;
                    if (this.attempt.status === 1) {
                        this.restoreAnswers();
                        this.startTimer();
                    }
                })
                .catch(err => {
                    if (err.response?.status === 404) this.notFound = true;
                })
                .finally(() => { this.loading = false; });
        },
        restoreAnswers() {
            if (!this.attempt?.answers) return;
            this.attempt.answers.forEach(a => {
                if (a.quizOptionId) {
                    this.selectedOptions[a.quizQuestionId] = a.quizOptionId;
                }
            });
        },
        startQuiz() {
            this.starting = true;
            const apiUrl = process.env.VUE_APP_API_URL;
            axios.post(`${apiUrl}quiz/take/${this.token}/start`)
                .then(() => {
                    this.attempt.status = 1;
                    this.questionStartTime = Date.now();
                    this.startTimer();
                })
                .catch(() => { alert('Failed to start quiz. Please try again.'); })
                .finally(() => { this.starting = false; });
        },
        startTimer() {
            const totalSeconds = (this.attempt.quiz.totalTimeMinutes ?? 30) * 60;
            this.totalTime = totalSeconds;

            // Calculate elapsed time if started
            if (this.attempt.startedAt) {
                const started = new Date(this.attempt.startedAt).getTime();
                const elapsed = Math.floor((Date.now() - started) / 1000);
                this.timeLeft = Math.max(0, totalSeconds - elapsed);
            } else {
                this.timeLeft = totalSeconds;
            }

            if (this.timeLeft <= 0) {
                this.autoComplete();
                return;
            }

            this.questionStartTime = Date.now();
            this.timerInterval = setInterval(() => {
                if (this.timeLeft <= 0) {
                    this.stopTimer();
                    this.autoComplete();
                } else {
                    this.timeLeft--;
                }
            }, 1000);
        },
        stopTimer() {
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
            }
        },
        recordTimeSpent() {
            if (!this.currentQuestion) return;
            const now = Date.now();
            const spent = Math.floor((now - (this.questionStartTime || now)) / 1000);
            const qId = this.currentQuestion.id;
            this.questionTimeSpent[qId] = (this.questionTimeSpent[qId] || 0) + spent;
            this.questionStartTime = now;
        },
        saveCurrentAnswer() {
            if (!this.currentQuestion) return;
            this.recordTimeSpent();
            const qId = this.currentQuestion.id;
            const apiUrl = process.env.VUE_APP_API_URL;
            axios.post(`${apiUrl}quiz/take/${this.token}/answer`, {
                quizQuestionId: qId,
                quizOptionId: this.selectedOptions[qId] ?? null,
                timeSpentSeconds: this.questionTimeSpent[qId] || 0
            }).catch(() => {});
        },
        nextQuestion() {
            this.saveCurrentAnswer();
            this.currentIndex++;
        },
        prevQuestion() {
            this.saveCurrentAnswer();
            this.currentIndex--;
        },
        confirmComplete() {
            this.saveCurrentAnswer();
            this.showConfirmComplete = true;
        },
        autoComplete() {
            this.saveCurrentAnswer();
            this.completeQuiz();
        },
        completeQuiz() {
            this.completing = true;
            this.showConfirmComplete = false;
            this.stopTimer();
            const apiUrl = process.env.VUE_APP_API_URL;
            axios.post(`${apiUrl}quiz/take/${this.token}/complete`)
                .then(res => {
                    this.attempt = res.data;
                })
                .catch(() => { alert('Failed to submit quiz. Please try again.'); this.completing = false; })
                .finally(() => { this.completing = false; });
        },
        formatTime(seconds) {
            const m = Math.floor(seconds / 60);
            const s = seconds % 60;
            return m > 0 ? `${m}m ${s}s` : `${s}s`;
        }
    }
};
</script>
